import { convertToYjs } from '@databases/collab/convert/to-collab';
import { type YDatabase } from '@databases/collab/y-database';
// eslint-disable-next-line workspaces/require-dependency
import {
  buildDatabaseFromCollabAndContent,
  buildDefaultValueConverters,
  getOrCreateCollabManager
} from '@databases/collab-api/collab/actions';
import { MigrationManager } from '@databases/collab-migrations/migrations-manager';
import { type ObjectMode } from '@databases/types';
import { type Content, type ContentType } from '@databases/types/confluence';
import {
  type BasicDatabase,
  type Database,
  type DatabaseOperations,
  type ShallowDatabase
} from '@databases/types/database';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';

import { getContentById, searchContent } from '../confluence/content.ts';
import { quoteCql, quoteFuzzyCql } from '../confluence/utils.ts';
import { DATABASE_CONTENT_TYPE } from '../constants.ts';
import { type CommonRootState, type SimpleStore } from '../redux/redux.ts';

/** The REST API expansions that need to be made when fetching a shallow database. */
const SHALLOW_DATABASE_EXPANSIONS = [
  'body.storage',
  'space',
  'history.lastUpdated',
  'version',
  'children.attachment',
  'operations'
];

/** The REST API expansions that need to be made when fetching a database. */
const DATABASE_EXPANSIONS = [
  ...SHALLOW_DATABASE_EXPANSIONS,
  'container' // For cases where we select a database by entriesContentId, to find out the database contentId
];

export async function createCollabDatabase(
  database: Database<ObjectMode.CREATE>,
  store: SimpleStore<CommonRootState>
): Promise<Database> {
  let contentId: string;
  try {
    const content = await getConfluenceService().createContent({
      title: database.name,
      spaceKey: database.space.key,
      type: 'database'
    });

    contentId = content.id;
  } catch (possibleError) {
    const error =
      possibleError instanceof Error
        ? possibleError
        : new Error(`Unknown error while creating new database: ${possibleError}`);

    throw error;
  }

  const databaseContent = await getDatabaseContentByContentId<'database'>(contentId);

  const ydb = convertToYjs(database, buildDefaultValueConverters(), {});

  // set to the latest migration version
  ydb.doc.getMap('_meta').set('version', MigrationManager.getClientDocVersion());

  const manager = await store.dispatch(
    getOrCreateCollabManager(
      {
        contentId,
        spaceId: databaseContent.space ? Number(databaseContent.space.id) : 0, // TODO: fix this, since space is expanded by getDatabaseContentByContentId
        referenceId: databaseContent.referenceId as string,
        isInitialLoad: false
      },
      ydb
    )
  );

  const db = await new Promise<YDatabase>((resolve) => {
    manager.onLoaded((db) => {
      resolve(db);
    });

    // kick off connection attempt
    manager.connect();
  });

  return buildDatabaseFromCollabAndContent(databaseContent, db, manager.isReadOnly);
}

export async function getDatabaseContentByContentId<T extends ContentType>(
  contentId: string
): Promise<Content<T>> {
  return await getContentById(contentId, DATABASE_EXPANSIONS);
}

export async function getDatabaseByContentId(
  contentId: string
): Promise<ShallowDatabase | BasicDatabase> {
  const content = await getContentById<'database'>(contentId, DATABASE_EXPANSIONS);
  if (!content) {
    throw new Error(`no content for database with content id ${contentId}`);
  }

  return getBasicCollabDatabaseFromContent(
    content,
    content.operations!.some(
      (op) => op.targetType === DATABASE_CONTENT_TYPE && op.operation === 'update'
    )
  );
}

async function searchDatabasesByCql(
  type: string,
  query?: string,
  spaceKey?: string
): Promise<(ShallowDatabase | BasicDatabase)[]> {
  const cql =
    [
      `type = ${quoteCql(type)}`,
      ...(spaceKey ? [`space=${quoteCql(spaceKey)}`] : []),
      `title~${quoteFuzzyCql(`${query}*`)}`
    ].join(' and ') + ' order by lastModified desc';

  const results: BasicDatabase[] = [];
  for await (const content of searchContent(cql, SHALLOW_DATABASE_EXPANSIONS, 20)) {
    if (content.type === DATABASE_CONTENT_TYPE) {
      const database = getBasicCollabDatabaseFromContent(
        content,
        content.operations!.some(
          (op) => op.targetType === DATABASE_CONTENT_TYPE && op.operation === 'update'
        )
      );
      results.push(database);
    }
  }
  return results;
}

/**
 * Search for databases by search term in the whole Confluence instance. A maximum of 20 databases is returned. Broken
 * databases are ignored.
 * @param query
 * @param spaceKey If specified, only return global databases and databases in this space. Otherwise, return databases
 *                 in any space.
 */
export async function searchDatabases(
  query?: string,
  spaceKey?: string
): Promise<BasicDatabase[]> {
  return await searchDatabasesByCql(DATABASE_CONTENT_TYPE, query, spaceKey);
}

export function getBasicCollabDatabaseFromContent(
  content: Content<'database'>,
  isReadOnly: boolean
) {
  const operations: DatabaseOperations = {
    updateMetadata: !isReadOnly,
    updateEntries: !isReadOnly,
    delete: !isReadOnly
  };

  const base: BasicDatabase = {
    contentId: content.id,
    contentVersion: content.version!.number,
    name: content.title,
    space: { key: content.space!.key, name: content.space!.name, id: content.space!.id },
    createdDate: content.history!.createdDate,
    createdBy: content.history!.createdBy,
    lastUpdatedDate: content.history?.lastUpdated?.when,
    lastUpdatedBy: content.history!.lastUpdated!.by,
    coverImage: undefined, // TODO
    operations,
    type: content.type
  };

  return base;
}
